import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types';
import ResultScopeContainer from '../containers/result_scope_container'
import EventHeader from './event_header';
import ChevronIcon from './chevron_icon';
import Loader from 'react-loader-spinner'
import { GlgTooltipIcon } from '../../shared/components';
import { themedTextClass } from '../utils/css_utils';
import ExpandedScorecardsTable from './expanded_scorecards_table';
import CutLineButton from './cut_line_button';
import PointsAndPurseButton from './points_and_purse_button';
import InfoTooltip from './info_tooltip';
import HeaderActionsContainer from '../containers/header_actions_container';
import { VIEW_MODES } from '../constants';
import { resolveMessageDescriptors, componentText } from '../utils/i18n_utils';
import useScrollReveal from '../../shared/hooks/useScrollReveal';
import useFlagsReady from '../hooks/useFlagsReady'

// Same text the legacy leaderboard shows for a tournament without results
// (its HAML hardcodes the English literal, so English-only is parity).
const t = (key, fallback) => componentText(`event.${key}`, fallback);

const noResultsMessage = () => t('no_results', 'No results yet.');

const Event = ({
  id,
  name,
  loading,
  error,
  data,
  isOpen,
  inProgress,
  adjusted,
  fetchResultScope,
  removeResultScope,
  handleMobileClick,
  viewMode,
  isSpec = false,
  isGroup = false,
  showExpandAll = false,
  holeByHoleView,
  displayedFlags,
  stateFlagUrls,
  isManagerSite,
  showExpandToggle = false,
  cutLineButton,
  pointsAndPurseButton,
  infoTooltip = [],
  currentRoundLabel = null,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isExpandedAll, setIsExpandedAll] = useState(false)
  const expandedScorecardsRef = useRef(null)

  // The all-scorecards table mounts below the full leaderboard — usually
  // off-screen — so without this the toggle appears to do nothing. 0.25 lands
  // the table's top a quarter down the display, covering the bottom 3/4.
  useScrollReveal(expandedScorecardsRef, isExpandedAll, { topRatio: 0.25 })

  function handleExpandClick() {
    setIsExpanded((isExpanded) => !isExpanded)
  }

  function handleExpandAllClick() {
    setIsExpandedAll((prev) => !prev)
  }


  function handleClick() {
    if (isOpen) {
      removeResultScope();
    } else {
      fetchResultScope();
    }
  }

  const scopes = data?.scopes;

  const hasScopes = Boolean(scopes && Object.keys(scopes).length)

  // Hold the rows until the flags would paint with them.
  const flagsReady = useFlagsReady(scopes, displayedFlags, stateFlagUrls)
  const awaitingFlags = hasScopes && !flagsReady

  // Nothing to show yet: either the tournament's own data is still coming
  // (picking another tournament clears the slice first), or it has arrived and
  // its flags have not.
  const isPending = awaitingFlags || (loading && !hasScopes)

  let content = null;
  if (isOpen) {
    if (error) content=null;
    else if (isPending) {
      // Web has the same wait in its header, so a second spinner in the body
      // would double up.
      content = viewMode === VIEW_MODES.WEB ? null : (
        <div className="lbv2__event-loading">
          <div className="lbv2__spinner" role="status" aria-label={componentText('shared.loading', 'Loading')} />
        </div>
      )
    } else if (hasScopes) {
      content = Object.keys(scopes).map((resultScopeId) => (
        <ResultScopeContainer
          key={resultScopeId}
          eventId={id}
          resultScopeId={resultScopeId}
          viewMode={viewMode}
          isExpanded={isExpanded}
        />
      ));
    } else if (data && !loading) {
      // A tournament without pairings returns no result scopes - show the
      // legacy leaderboard's message instead of an empty tile body. Polling
      // keeps running, so results appear once pairings are created.
      content = (
        <div className="lbv2__info-message">
          <i className="far fa-circle-info lbv2__info-message-icon" aria-hidden="true"></i>
          {noResultsMessage()}
        </div>
      );
    }
  }

  const showCurrentRound = !isSpec && !isManagerSite && !!currentRoundLabel
    && [VIEW_MODES.MOBILE, VIEW_MODES.LANDSCAPE].includes(viewMode);

  return (
    <li key={id} id={id} className={`lbv2__event ${isGroup ? 'in-group' : ''} ${isSpec ? 'is-spec' : ''}`}>
      <EventHeader
        isOpen={isOpen}
        className={showCurrentRound ? 'lbv2__event-header--with-round' : ''}
      >
        {isSpec ?
          <>
            <div className='lbv2__spec'>{name}</div>
            {viewMode !== VIEW_MODES.MOBILE && <PointsAndPurseButton button={pointsAndPurseButton} />}
          </>
          :
          <>
            <button
              className='lbv2__event-btn'
              onClick={[VIEW_MODES.MOBILE, VIEW_MODES.LANDSCAPE].includes(viewMode) ? handleMobileClick : handleClick}
              >
                {viewMode === VIEW_MODES.WEB && <ChevronIcon isOpen={isOpen}/> }
                <span className='lbv2__event-btn-label'>{name}</span>
                {/* Manager-only display-options warning, right after the name
                    like the HTML leaderboard's info circle; inside the button
                    so it stays next to a wrapped or ellipsized name. */}
                <InfoTooltip content={resolveMessageDescriptors(infoTooltip)} />
                {[VIEW_MODES.MOBILE, VIEW_MODES.LANDSCAPE].includes(viewMode) && <ChevronIcon isOpen={!isOpen}/> }
            </button>
            {viewMode !== VIEW_MODES.MOBILE && showExpandAll &&
              <button
                className={`lbv2__show-all-scores-btn ${themedTextClass(isManagerSite)}`}
                onClick={handleExpandAllClick}
              >
                {isExpandedAll ? t('hide_all_scores', 'Hide all scores') : t('show_all_scores', 'Show all scores')}
              </button>}
            {viewMode !== VIEW_MODES.MOBILE && <PointsAndPurseButton button={pointsAndPurseButton} />}
            {viewMode !== VIEW_MODES.MOBILE && <CutLineButton button={cutLineButton} />}
            {viewMode === VIEW_MODES.WEB && (inProgress || adjusted || loading || awaitingFlags || error) &&
              <div className='lbv2__state-info'>
                {inProgress &&
                  <div className='lbv2__in-progress'>{t('in_progress', 'In Progress')}</div>
                }
                {adjusted &&
                  <div className='lbv2__adjusted'>{t('adjusted', 'Adjusted')}</div>
                }
                {(loading || awaitingFlags) &&
                  <div className='lbv2__loader'>
                    <Loader type="Oval" color={ '#333F48' } height={ 20 } width={ 12 }></Loader>
                  </div>
                }
                {error &&
                  <div className='lbv2__error'>
                    <GlgTooltipIcon tooltip={ error } />
                    <div>{componentText('shared.error', 'error')}</div>
                  </div>
                }
              </div>
            }
            {[VIEW_MODES.MOBILE, VIEW_MODES.LANDSCAPE].includes(viewMode) && !isManagerSite &&
              <HeaderActionsContainer viewMode={viewMode} />}
            {showCurrentRound && <div className='lbv2__current-round'>{currentRoundLabel}</div>}
          </>
          }
      </EventHeader>
      {viewMode === VIEW_MODES.MOBILE && showExpandToggle &&
        <div className='lbv2__expand'>
          <button className={`lbv2__expand-btn ${themedTextClass(isManagerSite)}`} onClick={handleExpandClick}>
          {isExpanded ? t('collapse_view', 'Collapse View') : t('expand_view', 'Expand View')}
        </button>
        </div>}
      {content}
      {isExpandedAll && scopes &&
        <ExpandedScorecardsTable ref={expandedScorecardsRef} scopes={scopes} holeByHoleView={holeByHoleView} viewMode={viewMode} />}
    </li>
  )
};

Event.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.string,
  data: PropTypes.object,
  isOpen: PropTypes.bool,
  inProgress: PropTypes.bool,
  adjusted: PropTypes.bool,
  fetchResultScope: PropTypes.func,
  removeResultScope: PropTypes.func,
  handleMobileClick: PropTypes.func,
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)),
  isSpec: PropTypes.bool,
  isGroup: PropTypes.bool,
  showExpandAll: PropTypes.bool,
  holeByHoleView: PropTypes.string,
  displayedFlags: PropTypes.string,
  stateFlagUrls: PropTypes.objectOf(PropTypes.string),
  isManagerSite: PropTypes.bool,
  showExpandToggle: PropTypes.bool,
  cutLineButton: PropTypes.object,
  pointsAndPurseButton: PropTypes.object,
  infoTooltip: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    params: PropTypes.object,
  })),
  currentRoundLabel: PropTypes.string,
};

export default Event;
